import RCDrawer from 'rc-drawer';
import { IDrawerProps as RCDrawerProps } from 'rc-drawer/lib/IDrawerPropTypes';
import { createContext, ReactText, useContext, useEffect, useState } from 'react';
import { MuiTypographyProps } from '../typography/index';
import './drawer.less';

export interface DrawerProps extends RCDrawerProps {
	// open: boolean
	zIndex?: number;
	// Additional props that may be passed from ResponsiveDrawer but should not be forwarded to RCDrawer
	headerVariant?: MuiTypographyProps['variant'];
	closable?: boolean;
	visible?: boolean;
	destroyOnClose?: boolean;
	onKeepOpen?: () => void;
	maxWidthPercent?: number;
	forceZIndex?: number;
}

interface ParentDrawerAPI {
	depth: number;
	push: () => void;
	pull: () => void;
}

export const DrawerContext = createContext<ParentDrawerAPI | null>(null);

/** Obtain the drawer context. If non-null we're in a drawer */
export const useDrawerContext = () => useContext(DrawerContext);

export const Drawer = (props: DrawerProps) => {
	const { open } = props;
	const parentDrawer = useContext(DrawerContext);
	const [pushed, setPushed] = useState(false);
	const [offsetStyle, setOffsetStyle] = useState<{
		width?: ReactText;
		height?: ReactText;
	}>({});

	const push = () => setPushed(true);
	const pull = () => setPushed(false);

	const getPushTransform = (placement?: 'left' | 'right' | 'top' | 'bottom') => {
		if (placement === 'left' || placement === 'right') {
			return `translateX(${placement === 'left' ? 180 : -180}px)`;
		}
		if (placement === 'top' || placement === 'bottom') {
			return `translateY(${placement === 'top' ? 180 : -180}px)`;
		}
	};

	const getRcDrawerStyle = () => {
		const { zIndex, placement, style } = props;
		return {
			zIndex,
			transform: pushed ? getPushTransform(placement) : undefined,
			...style,
			outline: 0 // This prevents a focused vertical line on the left of the screen animating badly when closing drawer
		};
	};

	useEffect(() => {
		if (open) {
			parentDrawer?.push();
		} else {
			parentDrawer?.pull();
		}
		return () => parentDrawer?.pull();
	}, [open, parentDrawer]);

	useEffect(() => {
		if (props.placement === 'left' || props.placement === 'right') {
			setOffsetStyle({ width: props.width });
		} else {
			setOffsetStyle({ height: props.height });
		}
	}, [props.height, props.placement, props.width]);

	let mc = true;
	if (props.maskClosable == false) {
		mc = false;
	}
	
	// Extract custom props that shouldn't be passed to RCDrawer
	const {
		zIndex,
		headerVariant,
		closable,
		visible,
		destroyOnClose,
		onKeepOpen,
		maxWidthPercent,
		forceZIndex,
		...rcDrawerProps
	} = props;
	
	return (
		<DrawerContext.Provider value={{ push, pull, depth: (parentDrawer?.depth ?? 0) + 1 }}>
			<RCDrawer
				handler={props.handler ? props.handler : false}
				{...rcDrawerProps}
				{...offsetStyle}
				style={getRcDrawerStyle()}
				level={null}
				maskClosable={mc}
				contentWrapperStyle={props.contentWrapperStyle}
			/>
		</DrawerContext.Provider>
	);
};
